import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ILead } from 'src/services/api/leads/types';
import { apiClient } from 'src/services/api/client';
import { useNotifications } from 'src/notifications';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { queryKeys } from 'src/services/api/constants';

type IUpdateLeadsArchivedStatusesRequest = Array<Pick<ILead, 'uuid' | 'is_archived'>>;
type UpdateLeadStatusResponseSchema = {
    lead: ILead;
}

const updateLeadStatus = async (leadUuid: string, isArchived: boolean): Promise<ILead> => {
    const path = `${GET_LEADS_PATH}/${leadUuid}/status`;
    const json = { is_archived: isArchived };
    const res = await apiClient(path, { method: 'patch', json });
    return (await res.json<UpdateLeadStatusResponseSchema>()).lead;
};

export const useUpdateLeadsStatuses = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ILead[],
        IStandardErrorResponse,
        IUpdateLeadsArchivedStatusesRequest
    >(async (payload: IUpdateLeadsArchivedStatusesRequest) => {
        const updatedLeads = await Promise.all(payload.map(leadUpdate => {
            if (leadUpdate.is_archived === undefined) {
                throw new Error(`is_archived status is required, not set for lead ${leadUpdate.uuid}`);
            }

            return updateLeadStatus(leadUpdate.uuid, leadUpdate.is_archived);
        }));

        return updatedLeads;
    }, {
        onSuccess: (updatedLeads: ILead[]) => {
            queryClient.setQueryData<ILead[]>(queryKeys.leads, (oldData) => {
                if (!oldData) {
                    return updatedLeads;
                }

                const updatedLeadsMap = new Map(updatedLeads.map(lead => [lead.uuid, lead]));

                return oldData.map(lead => updatedLeadsMap.get(lead.uuid) || lead);
            });

            updatedLeads.forEach(updatedLead => {
                queryClient.setQueryData<ILead>([queryKeys.leads, updatedLead.uuid], updatedLead);
            });

            addNotification('Lead status updated', 'success');
        },
        onError: (error) => {
            queryClient.invalidateQueries(queryKeys.leads);
            const errorMessage = error.msg || 'Failed to update lead statuses';
            addNotification(errorMessage, 'error');
        }
    });
};
